import React, { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import { Alert, AlertDescription } from "../../../components/ui/alert";
import { Badge } from "../../../components/ui/badge";
import { AlertCircle, Edit3, Truck } from "lucide-react";
import { ShippingNoticeSettings } from "./ShippingNoticeSettings";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ShippingRuleCard = ({ rule, onUpdate, setSuccess, setError }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    base_cost: rule.base_cost,
    additional_cost: rule.additional_cost,
    base_quantity: rule.base_quantity || 4,
    additional_quantity: rule.additional_quantity || 4,
    delivery_days: rule.delivery_days,
    description: rule.description,
    // InPost specific fields
    enable_manual_pickup: rule.enable_manual_pickup || false,
    cost_italy: rule.cost_italy || rule.base_cost,
    cost_foreign: rule.cost_foreign || 7.00,
    insurance_cost: rule.insurance_cost || 3.00
  });
  const [updating, setUpdating] = useState(false);

  const isInPost = rule.id === 'inpost';

  const handleSave = async () => {
    try {
      setUpdating(true);
      const payload = { ...editData, base_quantity: parseInt(editData.base_quantity, 10) || 1, additional_quantity: parseInt(editData.additional_quantity, 10) || 1 };
      const response = await axios.put(`${API}/shipping-rules/${rule.id}`, payload);
      setSuccess(`Tariffe ${rule.country} aggiornate con successo!`);
      setIsEditing(false);
      setTimeout(() => setSuccess(""), 3000);
      onUpdate(); // Refresh the list
    } catch (error) {
      console.error("Error updating shipping rule:", error);
      setError("Errore nell'aggiornamento delle tariffe");
      setTimeout(() => setError(""), 3000);
    } finally {
      setUpdating(false);
    }
  };

  const handleCancel = () => {
    setEditData({
      base_cost: rule.base_cost,
      additional_cost: rule.additional_cost,
      base_quantity: rule.base_quantity || 4,
      additional_quantity: rule.additional_quantity || 4,
      delivery_days: rule.delivery_days,
      description: rule.description,
      enable_manual_pickup: rule.enable_manual_pickup || false,
      cost_italy: rule.cost_italy || rule.base_cost,
      cost_foreign: rule.cost_foreign || 7.00,
      insurance_cost: rule.insurance_cost || 3.00
    });
    setIsEditing(false);
  };

  // Safety check
  if (!rule || typeof rule !== 'object') {
    return null;
  }

  return (
    <Card className={isInPost ? "border-2 border-blue-400" : ""}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center space-x-2">
            <span className="text-3xl">{rule.icon || '🌍'}</span>
            <span>{rule.country || 'N/A'}</span>
            <Badge variant="secondary">{rule.zone || 'N/A'}</Badge>
            {isInPost && <Badge className="bg-blue-500">⚙️ Speciale</Badge>}
          </CardTitle>
          {!isEditing ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEditing(true)}
              className="flex items-center space-x-1"
            >
              <Edit3 className="w-4 h-4" />
              <span>Modifica</span>
            </Button>
          ) : null}
        </div>
      </CardHeader>
      <CardContent>
        {isEditing ? (
          <div className="space-y-4">
            {isInPost ? (
              // InPost specific edit form
              <>
                <Alert className="bg-blue-50 border-blue-200">
                  <AlertCircle className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-800">
                    <strong>InPost - Servizio Hub to Point:</strong> Gestisci i costi per Italia ed estero. 
                    <div className="mt-2 space-y-1 text-xs">
                      <div>🛡️ <strong>Assicurazione BASE €25 INCLUSA</strong> in ogni spedizione</div>
                      <div>📦 Max 41x38x64 cm (Grande) - Peso max 25kg</div>
                      <div>⚠️ Sicilia/Sardegna: +€1.00 • Repackaging: +€3.00</div>
                    </div>
                  </AlertDescription>
                </Alert>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor={`cost-italy-${rule.id}`}>💰 Costo Italia (€) *</Label>
                    <Input
                      id={`cost-italy-${rule.id}`}
                      type="number"
                      step="0.01"
                      min="0"
                      value={editData.cost_italy}
                      onChange={(e) => setEditData({...editData, cost_italy: parseFloat(e.target.value)})}
                    />
                  </div>
                  <div>
                    <Label htmlFor={`cost-foreign-${rule.id}`}>🌍 Costo Estero (€) *</Label>
                    <Input
                      id={`cost-foreign-${rule.id}`}
                      type="number"
                      step="0.01"
                      min="0"
                      value={editData.cost_foreign}
                      onChange={(e) => setEditData({...editData, cost_foreign: parseFloat(e.target.value)})}
                    />
                  </div>
                  <div>
                    <Label htmlFor={`insurance-${rule.id}`}>🛡️ Assicurazione (€) *</Label>
                    <Input
                      id={`insurance-${rule.id}`}
                      type="number"
                      step="0.01"
                      min="0"
                      value={editData.insurance_cost}
                      onChange={(e) => setEditData({...editData, insurance_cost: parseFloat(e.target.value)})}
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                  <input
                    type="checkbox"
                    id={`enable-pickup-${rule.id}`}
                    checked={editData.enable_manual_pickup}
                    onChange={(e) => setEditData({...editData, enable_manual_pickup: e.target.checked})}
                    className="w-5 h-5 text-blue-600 rounded"
                  />
                  <Label htmlFor={`enable-pickup-${rule.id}`} className="cursor-pointer text-base">
                    ✅ Abilita campo manuale "Punto di Ritiro InPost" nel checkout
                  </Label>
                </div>

                <div>
                  <Label htmlFor={`delivery-${rule.id}`}>🚚 Tempi di consegna *</Label>
                  <Input
                    id={`delivery-${rule.id}`}
                    type="text"
                    value={editData.delivery_days}
                    onChange={(e) => setEditData({...editData, delivery_days: e.target.value})}
                    placeholder="es. 2-3 giorni lavorativi"
                  />
                </div>
                <div>
                  <Label htmlFor={`description-${rule.id}`}>📝 Descrizione *</Label>
                  <Textarea
                    id={`description-${rule.id}`}
                    value={editData.description}
                    onChange={(e) => setEditData({...editData, description: e.target.value})}
                    rows={3}
                  />
                </div>
              </>
            ) : (
              // Standard shipping rule edit form
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor={`base-cost-${rule.id}`}>Costo base (€) *</Label>
                    <Input
                      id={`base-cost-${rule.id}`}
                      type="number"
                      step="0.01"
                      min="0"
                      value={editData.base_cost}
                      onChange={(e) => setEditData({...editData, base_cost: parseFloat(e.target.value)})}
                    />
                  </div>
                  <div>
                    <Label htmlFor={`additional-cost-${rule.id}`}>Costo aggiuntivo (€) *</Label>
                    <Input
                      id={`additional-cost-${rule.id}`}
                      type="number"
                      step="0.01"
                      min="0"
                      value={editData.additional_cost}
                      onChange={(e) => setEditData({...editData, additional_cost: parseFloat(e.target.value)})}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor={`base-qty-${rule.id}`}>Articoli per costo base</Label>
                    <Input
                      id={`base-qty-${rule.id}`}
                      type="number"
                      min="1"
                      value={editData.base_quantity}
                      onChange={(e) => setEditData({...editData, base_quantity: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor={`add-qty-${rule.id}`}>Articoli per scatto aggiuntivo</Label>
                    <Input
                      id={`add-qty-${rule.id}`}
                      type="number"
                      min="1"
                      value={editData.additional_quantity}
                      onChange={(e) => setEditData({...editData, additional_quantity: e.target.value})}
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor={`delivery-${rule.id}`}>Tempi di consegna *</Label>
                  <Input
                    id={`delivery-${rule.id}`}
                    type="text"
                    value={editData.delivery_days}
                    onChange={(e) => setEditData({...editData, delivery_days: e.target.value})}
                    placeholder="es. 3-5 giorni lavorativi"
                  />
                </div>
                <div>
                  <Label htmlFor={`description-${rule.id}`}>Descrizione *</Label>
                  <Textarea
                    id={`description-${rule.id}`}
                    value={editData.description}
                    onChange={(e) => setEditData({...editData, description: e.target.value})}
                    rows={2}
                  />
                </div>
              </>
            )}
            
            <div className="flex space-x-2 pt-4">
              <Button
                onClick={handleSave}
                disabled={updating}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                {updating ? "Salvataggio..." : "Salva Modifiche"}
              </Button>
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={updating}
                className="flex-1"
              >
                Annulla
              </Button>
            </div>
          </div>
        ) : (
          <>
            {isInPost ? (
              // InPost specific display
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">💰 Tariffe InPost</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between py-2 border-b border-gray-100">
                        <span className="text-gray-600">🇮🇹 Italia (Hub to Point):</span>
                        <span className="font-medium text-green-600">€{(rule.cost_italy || rule.base_cost).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-gray-100">
                        <span className="text-gray-600">🌍 Estero (8 paesi EU):</span>
                        <span className="font-medium text-blue-600">€{(rule.cost_foreign || 7.00).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-gray-100 bg-green-50">
                        <span className="text-gray-800 font-medium">🛡️ Assicurazione BASE:</span>
                        <span className="font-bold text-green-700">€25 INCLUSA</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-gray-100">
                        <span className="text-gray-600">🛡️ Assicurazione aggiuntiva:</span>
                        <span className="font-medium text-orange-600">+€{(rule.insurance_cost || 3.00).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">📦 Specifiche Servizio</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-start py-2">
                        <Truck className="w-4 h-4 text-gray-400 mt-0.5 mr-2 flex-shrink-0" />
                        <div>
                          <span className="text-gray-600">Tempi di consegna:</span>
                          <p className="font-medium text-gray-900">{rule.delivery_days}</p>
                        </div>
                      </div>
                      <div className="py-2">
                        <span className="text-gray-600">Tipo Servizio:</span>
                        <p className="font-medium text-gray-900">{rule.service_type === 'hub_to_point' ? 'Hub to Point' : 'Standard'}</p>
                      </div>
                      <div className="py-2">
                        <span className="text-gray-600">📏 Dimensioni Max:</span>
                        <p className="font-medium text-gray-900">{rule.max_dimensions || '41x38x64 cm'}</p>
                      </div>
                      <div className="py-2">
                        <span className="text-gray-600">⚖️ Peso Max:</span>
                        <p className="font-medium text-gray-900">{rule.max_weight || 25} kg</p>
                      </div>
                      <div className="py-2">
                        <p className="text-gray-600">Campo Pickup Point:</p>
                        <div className="font-medium text-gray-900 mt-1">
                          {rule.enable_manual_pickup ? (
                            <Badge className="bg-green-100 text-green-800">✅ Abilitato</Badge>
                          ) : (
                            <Badge className="bg-gray-100 text-gray-600">❌ Disabilitato</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h5 className="font-semibold text-blue-900 mb-2">📝 Descrizione Servizio:</h5>
                  <p className="text-sm text-blue-800 mb-3">{rule.description}</p>
                  {rule.special_notes && (
                    <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded text-xs text-orange-900">
                      <strong>⚠️ Note Speciali:</strong> {rule.special_notes}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              // Standard shipping rule display
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Tariffe</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between py-2 border-b border-gray-100">
                        <span className="text-gray-600">Costo base (1-{rule.base_quantity || 4} articoli):</span>
                        <span className="font-medium text-green-600">€{rule.base_cost.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-gray-100">
                        <span className="text-gray-600">Costo aggiuntivo (ogni {rule.additional_quantity || 4} articoli):</span>
                        <span className="font-medium text-blue-600">+€{rule.additional_cost.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Informazioni</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-start py-2">
                        <Truck className="w-4 h-4 text-gray-400 mt-0.5 mr-2 flex-shrink-0" />
                        <div>
                          <span className="text-gray-600">Tempi di consegna:</span>
                          <p className="font-medium text-gray-900">{rule.delivery_days}</p>
                        </div>
                      </div>
                      <div className="py-2">
                        <span className="text-gray-600">Descrizione:</span>
                        <p className="font-medium text-gray-900 mt-1">{rule.description}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <h5 className="font-semibold text-gray-900 mb-2">Esempi di calcolo:</h5>
                  <div className="space-y-1 text-sm text-gray-600">
                    {(() => {
                      const bq = rule.base_quantity || 4;
                      const aq = rule.additional_quantity || 4;
                      const bc = rule.base_cost || 0;
                      const ac = rule.additional_cost || 0;
                      const calc = (n) => {
                        if (n <= bq) return bc;
                        const groups = Math.ceil((n - bq) / aq);
                        return bc + groups * ac;
                      };
                      const examples = [1, bq, bq + 1, bq + aq, bq + aq + 1];
                      const unique = [...new Set(examples)].sort((a, b) => a - b);
                      return unique.map(n => (
                        <p key={n}>• {n} articol{n === 1 ? 'o' : 'i'}: <strong className="text-gray-900">€{calc(n).toFixed(2)}</strong></p>
                      ));
                    })()}
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Create Shipping Rule Modal Component
export const CreateShippingRuleModal = ({ onClose, onSuccess, setError }) => {
  const [formData, setFormData] = useState({
    country: "",
    zone: "",
    icon: "🌍",
    base_cost: "",
    additional_cost: "",
    base_quantity: "4",
    additional_quantity: "4",
    delivery_days: "",
    description: ""
  });
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    try {
      // Validate required fields
      if (!formData.country || !formData.zone || !formData.base_cost || 
          !formData.additional_cost || !formData.delivery_days || !formData.description) {
        setError("Tutti i campi sono obbligatori");
        setTimeout(() => setError(""), 3000);
        return;
      }

      setCreating(true);
      const token = sessionStorage.getItem('access_token');
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shipping-rules`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          country: formData.country,
          zone: formData.zone,
          icon: formData.icon,
          base_cost: parseFloat(formData.base_cost),
          additional_cost: parseFloat(formData.additional_cost),
          base_quantity: parseInt(formData.base_quantity) || 4,
          additional_quantity: parseInt(formData.additional_quantity) || 4,
          delivery_days: formData.delivery_days,
          description: formData.description
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Errore nella creazione');
      }

      onSuccess();
    } catch (error) {
      console.error("Error creating shipping rule:", error);
      setError(error.message || "Errore nella creazione della regola");
      setTimeout(() => setError(""), 3000);
    } finally {
      setCreating(false);
    }
  };

  const commonIcons = ["🇮🇹", "🇪🇺", "🌍", "🌎", "🌏", "🇩🇪", "🇫🇷", "🇪🇸", "🇬🇧", "🇺🇸"];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-white">Crea Nuova Regola di Spedizione</h2>
            <Button variant="outline" onClick={onClose}>✕</Button>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="country">Paese / Zona *</Label>
                <Input
                  id="country"
                  type="text"
                  value={formData.country}
                  onChange={(e) => setFormData({...formData, country: e.target.value})}
                  placeholder="es. Germania, Europa"
                />
              </div>
              <div>
                <Label htmlFor="zone">Zona ID *</Label>
                <Input
                  id="zone"
                  type="text"
                  value={formData.zone}
                  onChange={(e) => setFormData({...formData, zone: e.target.value})}
                  placeholder="es. eu, de, us"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="icon">Icona Bandiera *</Label>
              <div className="flex flex-wrap gap-2 mb-2">
                {commonIcons.map((icon) => (
                  <button
                    key={icon}
                    type="button"
                    onClick={() => setFormData({...formData, icon})}
                    className={`text-2xl p-2 rounded border ${
                      formData.icon === icon 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {icon}
                  </button>
                ))}
              </div>
              <Input
                id="icon"
                type="text"
                value={formData.icon}
                onChange={(e) => setFormData({...formData, icon: e.target.value})}
                placeholder="o inserisci emoji personalizzata"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="base_cost">Costo base (€) *</Label>
                <Input
                  id="base_cost"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.base_cost}
                  onChange={(e) => setFormData({...formData, base_cost: e.target.value})}
                  placeholder="es. 9.00"
                />
                <p className="text-xs text-gray-500 mt-1">Per i primi 1-{formData.base_quantity || 4} articoli</p>
              </div>
              <div>
                <Label htmlFor="additional_cost">Costo aggiuntivo (€) *</Label>
                <Input
                  id="additional_cost"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.additional_cost}
                  onChange={(e) => setFormData({...formData, additional_cost: e.target.value})}
                  placeholder="es. 5.00"
                />
                <p className="text-xs text-gray-500 mt-1">Per ogni ulteriori {formData.additional_quantity || 4} articoli</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="base_quantity">Articoli per costo base</Label>
                <Input
                  id="base_quantity"
                  type="number"
                  min="1"
                  value={formData.base_quantity}
                  onChange={(e) => setFormData({...formData, base_quantity: e.target.value})}
                />
                <p className="text-xs text-gray-500 mt-1">Numero articoli coperti dal costo base</p>
              </div>
              <div>
                <Label htmlFor="additional_quantity">Articoli per scatto aggiuntivo</Label>
                <Input
                  id="additional_quantity"
                  type="number"
                  min="1"
                  value={formData.additional_quantity}
                  onChange={(e) => setFormData({...formData, additional_quantity: e.target.value})}
                />
                <p className="text-xs text-gray-500 mt-1">Ogni quanti articoli scatta il costo aggiuntivo</p>
              </div>
            </div>

            <div>
              <Label htmlFor="delivery_days">Tempi di consegna *</Label>
              <Input
                id="delivery_days"
                type="text"
                value={formData.delivery_days}
                onChange={(e) => setFormData({...formData, delivery_days: e.target.value})}
                placeholder="es. 3-5 giorni lavorativi"
              />
            </div>

            <div>
              <Label htmlFor="description">Descrizione *</Label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="es. €9 per 1-4 articoli, poi +€5 ogni 1-4 articoli successivi"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <Alert className="bg-blue-50 border-blue-200">
              <AlertCircle className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-800 text-sm">
                <strong>Nota:</strong> Il sistema calcola automaticamente la spedizione in base alla quantità. 
                I primi articoli (fino al numero impostato) costano il "Costo base", poi ogni gruppo successivo aggiunge il "Costo aggiuntivo".
              </AlertDescription>
            </Alert>
          </div>

          <div className="flex space-x-3 mt-6">
            <Button
              onClick={handleCreate}
              disabled={creating}
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              {creating ? "Creazione..." : "✅ Crea Regola"}
            </Button>
            <Button
              variant="outline"
              onClick={onClose}
              disabled={creating}
              className="flex-1"
            >
              Annulla
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

/** Admin Shipping tab body — Manage shipping rules per zone */
export const ShippingTab = ({ shippingRulesAdmin, loading, setShowCreateShippingModal, fetchShippingRules, setSuccess, setError }) => {
  const [syncing, setSyncing] = useState(false);

  const handleSyncEbay = async () => {
    if (!window.confirm("Sincronizzare le tariffe Italia + Europa dal sito alla Fulfillment Policy eBay?\n\nVerranno aggiornati SOLO i costi di spedizione, non le esclusioni/servizi/handling time.")) return;
    setSyncing(true);
    try {
      const token = sessionStorage.getItem("access_token");
      const r = await axios.post(`${API}/ebay/sync-shipping-policy`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const zones = (r.data?.updated_zones || [])
        .map((z) => `${z.type === "DOMESTIC" ? "🇮🇹" : "🇪🇺"} €${z.price.toFixed(2)}${z.extra > 0 ? ` (+€${z.extra.toFixed(2)} extra)` : ""}`)
        .join(" · ");
      setSuccess(`✅ Policy eBay "${r.data?.policy_name || ""}" sincronizzata: ${zones}`);
      setTimeout(() => setSuccess(""), 5000);
    } catch (e) {
      setError(`Errore sync eBay: ${e?.response?.data?.detail || e.message}`);
      setTimeout(() => setError(""), 5000);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white">Gestione Spedizioni</h2>
          <p className="text-gray-400">Configura le tariffe di spedizione per ogni zona</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleSyncEbay}
            disabled={syncing}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="shipping-sync-ebay-btn"
            title="Sincronizza tariffe Italia + Europa dal sito alla policy eBay"
          >
            {syncing ? "🔄 Sincronizzazione..." : "🟦 Sincronizza con eBay"}
          </Button>
          <Button onClick={() => setShowCreateShippingModal(true)} className="bg-green-600 hover:bg-green-700">
            ➕ Crea Nuova Regola
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Caricamento...</p>
        </div>
      ) : (
        <div className="space-y-6">
          <ShippingNoticeSettings setSuccess={setSuccess} setError={setError} />
          <Alert className="bg-blue-50 border-blue-200">
            <AlertCircle className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800">
              <strong>Sistema di spedizione basato sulla quantità:</strong> Le tariffe vengono calcolate automaticamente
              in base al numero di articoli nel carrello. Ogni zona ha un costo base per i primi 4 articoli,
              poi un costo aggiuntivo per ogni ulteriori 1-4 articoli.
            </AlertDescription>
          </Alert>

          {shippingRulesAdmin.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <Truck className="w-24 h-24 mx-auto" />
              </div>
              <h3 className="text-xl font-medium text-gray-200 mb-2">Nessuna regola di spedizione</h3>
              <p className="text-gray-400 mb-6">Crea la tua prima regola di spedizione!</p>
            </div>
          ) : (
            shippingRulesAdmin.map((rule) => (
              <ShippingRuleCard
                key={rule.id}
                rule={rule}
                onUpdate={fetchShippingRules}
                setSuccess={setSuccess}
                setError={setError}
              />
            ))
          )}
        </div>
      )}
    </>
  );
};
